/**
 * abhaMatchUtils.js
 *
 * Pure demographic-matching utilities for the ABHA "Link" flow.
 * No React / component dependencies — safe to import anywhere.
 *
 * Matching philosophy:
 *   - Name is the primary signal.  We do token-level matching so
 *     "Ravi Kumar Sharma" still matches "Ravi Sharma" (middle name gap).
 *   - We only consider tokens of >= 4 chars to avoid false positives
 *     on common short words ("Ali", "Das", etc.).
 *   - Gender + DOB year are additive bonuses.
 *   - Threshold: score >= 2 -> MATCH.  A name token hit alone (score 2)
 *     is sufficient; the receptionist sees both sides and can override.
 */

/**
 * Normalise a name string for comparison:
 *   - lowercase
 *   - collapse whitespace
 *   - strip non-alphanumeric characters (handles punctuation, hyphens)
 *
 * @param {string} name
 * @returns {string}
 */
export function normalizeName(name = '') {
  return String(name)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Normalise a gender value to one of 'male' | 'female' | 'other' | ''.
 *
 * ABHA profiles return 'M' / 'F' / 'O' or full English words.
 * Local patient records store full English words.
 *
 * @param {string} gender
 * @returns {string}
 */
export function normalizeGender(gender = '') {
  const g = String(gender).toLowerCase().trim();
  if (g === 'm' || g === 'male') return 'male';
  if (g === 'f' || g === 'female') return 'female';
  if (g === 'o' || g === 'other') return 'other';
  return '';
}

/**
 * Extract a 4-digit year from a date value.
 * Accepts: dayjs object, ISO string, 'DD-MM-YYYY', 'YYYY-MM-DD'.
 *
 * @param {*} dob
 * @returns {string|null}  4-digit year string or null
 */
export function extractYear(dob) {
  if (!dob) return null;

  // dayjs object
  if (typeof dob.year === 'function') {
    const y = dob.year();
    return y > 1900 ? String(y) : null;
  }

  const s = String(dob).trim();
  if (!s) return null;

  // ISO or YYYY-MM-DD
  const isoMatch = s.match(/^(\d{4})-\d{2}-\d{2}/);
  if (isoMatch) return isoMatch[1];

  // DD-MM-YYYY
  const dmyMatch = s.match(/^\d{2}-\d{2}-(\d{4})$/);
  if (dmyMatch) return dmyMatch[1];

  // Plain year
  const yearOnly = s.match(/^(\d{4})$/);
  if (yearOnly) return yearOnly[1];

  return null;
}

/**
 * Score how closely an ABHA profile matches a local patient record.
 *
 * @param {object} abhaProfile   - Formatted profile from formatAbhaProfile().
 *   Expected fields: firstName, middleName, lastName, gender, dateOfBirth
 * @param {object} localPatient  - Local DB patient row.
 *   Expected fields: full_name, gender, dob
 *
 * @returns {{ isMatch: boolean, score: number, reasons: string[] }}
 */
export function scoreAbhaMatch(abhaProfile, localPatient) {
  if (!abhaProfile || !localPatient) {
    return { isMatch: false, score: 0, reasons: ['Missing profile or patient data'] };
  }

  let score = 0;
  const reasons = [];

  // -- 1. Name token matching ------------------------------------------------
  const abhaFullName = normalizeName(
    [abhaProfile.firstName, abhaProfile.middleName, abhaProfile.lastName]
      .filter(Boolean)
      .join(' ')
  );
  const localFullName = normalizeName(localPatient.full_name || '');

  const abhaTokens = abhaFullName.split(' ').filter((t) => t.length >= 4);
  const localTokens = localFullName.split(' ').filter((t) => t.length >= 4);

  const nameHit =
    abhaTokens.length > 0 &&
    localTokens.length > 0 &&
    abhaTokens.some((at) =>
      localTokens.some((lt) => at === lt || at.includes(lt) || lt.includes(at))
    );

  if (nameHit) {
    score += 2;
    reasons.push('Name tokens match');
  } else if (abhaTokens.length === 0 || localTokens.length === 0) {
    // Fallback for very short names: full-string containment check
    if (
      abhaFullName &&
      localFullName &&
      (abhaFullName.includes(localFullName) || localFullName.includes(abhaFullName))
    ) {
      score += 2;
      reasons.push('Name substring match (short names)');
    }
  }

  // -- 2. Gender match -------------------------------------------------------
  const abhaGender = normalizeGender(abhaProfile.gender);
  const localGender = normalizeGender(localPatient.gender);

  if (abhaGender && localGender && abhaGender === localGender) {
    score += 1;
    reasons.push('Gender matches');
  }

  // -- 3. DOB year match -----------------------------------------------------
  const abhaYear = extractYear(abhaProfile.dateOfBirth);
  const localYear = extractYear(localPatient.dob);

  if (abhaYear && localYear && abhaYear === localYear) {
    score += 1;
    reasons.push('Birth year matches');
  }

  return {
    isMatch: score >= 2,
    score,
    reasons,
  };
}
